import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ABORT_ERROR, makeApiRequests } from '../../helpers/api';
import {
    ENDPOINTS,
    SITE_IMPROVEMENT_ALLOWANCES_DOUBLE_TRIPLE_WIDE,
    SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE
} from '../../helpers/constants';
import { X } from 'react-bootstrap-icons';
import { v4 as uuid } from 'uuid';
import FilePopup from '../common/FilePopup';
import UnderlineButton from '../common/UnderlineButton';
import { getModelOptions } from '../../helpers/formHelpers';
import { updateSizesAccordingToSections } from '../../helpers/global';
import './CalculationWorksheet.scss';

export const SKIRTING_TYPE_OPTIONS = ['Vinyl', 'Metal', 'Hardie Board/Insulated', 'Masonry'];

export function getSkirtingAllowanceByType(skirtingType, isSingleWide) {
    if (!skirtingType) return null;
    const data = isSingleWide ? SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE : SITE_IMPROVEMENT_ALLOWANCES_DOUBLE_TRIPLE_WIDE;
    const row = data.find((r) => r.item && r.item === skirtingType);
    return row ? row.max : null;
}

// Get max allowance by item name from Single or Double/Triple Wide table.
export function getAllowanceByItemName(itemName, isSingleWide) {
    const data = isSingleWide ? SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE : SITE_IMPROVEMENT_ALLOWANCES_DOUBLE_TRIPLE_WIDE;
    const row = data.find((r) => r.item && r.item === itemName);
    return row ? row.max : null;
}

/** Default ADDS field values from the site improvement allowance table (ignores inventory). */
export function getAllowanceAddFieldDefaults(isSingleWide, numberOfSets = 0, skirtingType = '') {
    const allowanceStr = (itemName) => {
        const v = getAllowanceByItemName(itemName, isSingleWide);
        return v != null ? String(v) : '';
    };
    const sets = parseInt(numberOfSets, 10) || 0;
    const stepsPerSet = getAllowanceByItemName('Steps & Decks (Per Set)', isSingleWide) ?? 0;
    const skirtingAllowance = getSkirtingAllowanceByType(skirtingType, isSingleWide);
    return {
        addAirConditioner: allowanceStr('Air Conditioner'),
        addDeliveryAndSet: allowanceStr('Delivery & Set'),
        addDirtPad: allowanceStr('Pad'),
        addFooters: allowanceStr('Footers'),
        addTrimOutTapeAndTexture: allowanceStr('Trim Out'),
        addElectricHookup: allowanceStr('Electric Hookups'),
        addWaterHookup: allowanceStr('Water Hookups'),
        addSewerHookup: allowanceStr('Sewer Hookups'),
        addGasHookup: allowanceStr('Gas Hookups'),
        addGuttersAndDownspouts: allowanceStr('Gutters & Downspouts'),
        addStepsDecks: String(stepsPerSet * sets),
        addSkirting: skirtingAllowance != null ? String(skirtingAllowance) : '',
    };
}

// Helper to safely read nested values
const get = (obj, path, fallback) => {
    if (!obj || !path) return fallback;
    const parts = path.split('.');
    let current = obj;
    for (const part of parts) {
        if (current && Object.prototype.hasOwnProperty.call(current, part)) {
            current = current[part];
        } else {
            return fallback;
        }
    }
    return current ?? fallback;
};

/** Read nested value when the path exists; `found: false` if any segment is missing. */
const getRaw = (obj, path) => {
    if (!obj || !path) return { found: false, value: undefined };
    const parts = path.split('.');
    let current = obj;
    for (const part of parts) {
        if (current != null && Object.prototype.hasOwnProperty.call(current, part)) {
            current = current[part];
        } else {
            return { found: false, value: undefined };
        }
    }
    return { found: true, value: current };
};

const LAND_IMPROVEMENT_LINE_KEYS = [
    'basement', 'grading', 'brickBlockSkirting', 'permanentFoundation', 'carport', 'permits',
    'deck', 'porch', 'driveway', 'septic', 'electricalHookup', 'sewerHookup', 'footers',
    'waterHookup', 'garage', 'well', 'gasHookup', 'other'
];

/** Sum numeric land improvement lines when no separate Amount Desired override exists on inventory. */
const sumInventoryLandImprovementLines = (inventory) => {
    const li = inventory?.maxSalesPriceLandImprovements;
    if (!li || typeof li !== 'object') return '';
    let t = 0;
    for (const k of LAND_IMPROVEMENT_LINE_KEYS) {
        const v = li[k];
        if (v != null && v !== '') {
            const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''));
            if (Number.isFinite(n)) t += n;
        }
    }
    return t === 0 ? '' : String(t);
};

// Central place to map worksheet "inventory fields" to actual data paths.
// Prefill: apply defaults only when the stored path is missing (undefined). Persisted 0, null, and '' are kept as-is.
export const buildInventoryPrefill = (inventory) => {
    if (!inventory) return {};

    const isSingleWide = (inventory.numberOfSections ?? 1) === 1;
    const g = (path, defaultValue) => {
        const { found, value } = getRaw(inventory, path);
        if (!found) return defaultValue;
        if (value === null || value === '') return '';
        return value;
    };

    const manufacturerName =
        get(inventory, 'manufacturer.name') ||
        get(inventory, 'inventoryUnit.manufacturer.name', '');

    const stepsPerSetAllowance = getAllowanceByItemName('Steps & Decks (Per Set)', isSingleWide) ?? 0;
    const numberOfSets = (parseInt(g('maxSalesPriceCalculatorAddFields.numberOfSets', 0), 10) || 0);

    return {
        // CalculationAmountDesired section
        salesPrice: inventory.salesPrice ?? '',
        tradeInGrossAllowance: inventory.tradeInGrossAllowance ?? get(inventory, 'contract.tradeInGrossAllowance', ''),
        lessBalanceDue: inventory.lessBalanceDue ?? get(inventory, 'contract.lessBalanceDue', ''),
        downPayment: inventory.downPayment ?? get(inventory, 'contract.downPayment', ''),
        insuranceCosts: inventory.insuranceCosts ?? get(inventory, 'contract.insuranceCosts', ''),
        amountDesiredLandImprovements: (() => {
            const raw = inventory.amountDesiredLandImprovements;
            if (raw != null && raw !== '') return raw;
            return sumInventoryLandImprovementLines(inventory);
        })(),

        // MaxSalesPrice section
        invoice: get(inventory, 'invoice', undefined) ?? '',
        freight: g('freight', get(inventory, 'maxSalesPriceCalculatorDeletedFields.factoryFreight', undefined)) ?? '',
        stateTax: g('stateTax', get(inventory, 'maxSalesPriceCalculatorDeletedFields.taxFromInvoice', undefined)) ?? '',
        vepCode: ((String(inventory.vepCode ?? get(inventory, 'inventoryUnit.vepCode', '')).trim()) || '0'),

        // Deletions — Wheels & Axles: 500 (single) / 1000 (double-triple) when null/undefined
        wheelsAndAxles: g('maxSalesPriceCalculatorDeletedFields.wheelsAndAxles', isSingleWide ? 500 : 1000),
        airConditioner: g('maxSalesPriceCalculatorDeletedFields.airConditioner', ''),
        hudDuesFees: g('maxSalesPriceCalculatorDeletedFields.hudDuesFees', ''),
        furnitureEntertainmentPack: g('maxSalesPriceCalculatorDeletedFields.furnitureEntertainmentPack', ''),
        otherPacksSalesAllowances: g('maxSalesPriceCalculatorDeletedFields.otherPacksSalesAllowances', ''),
        dealerRebate: g('maxSalesPriceCalculatorDeletedFields.dealerRebate', ''),
        advertisingEnvironmentalAllowance: g('maxSalesPriceCalculatorDeletedFields.advertisingEnvironmentalAllowance', ''),
        trimOutTapeAndTexture: g('maxSalesPriceCalculatorDeletedFields.trimOutTapeAndTexture', ''),
        otherDeletion: g('maxSalesPriceCalculatorDeletedFields.other', ''),
        taxFromInvoice: g('maxSalesPriceCalculatorDeletedFields.taxFromInvoice', inventory.stateTax) ?? '',

        // Adds — from unit or allowance table (get(..., undefined) ?? getAllowanceByItemName(...) or '')
        addFactoryFreight: g('maxSalesPriceCalculatorAddFields.factoryFreight', inventory.freight) ?? '',
        addAirConditioner: g('maxSalesPriceCalculatorAddFields.airConditioner', getAllowanceByItemName('Air Conditioner', isSingleWide)) ?? '',
        addTaxFromInvoice: g('maxSalesPriceCalculatorAddFields.taxFromInvoice', inventory.stateTax) ?? '',
        addHudDuesFees: g('maxSalesPriceCalculatorAddFields.hudDuesFees', 350),
        addDeliveryAndSet: g('maxSalesPriceCalculatorAddFields.deliveryAndSet', inventory.deliveryAndSet ?? getAllowanceByItemName('Delivery & Set', isSingleWide)) ?? '',
        addSkirtingType: g('maxSalesPriceCalculatorAddFields.skirtingType', ''),
        addSkirting: (() => {
            const stored = getRaw(inventory, 'maxSalesPriceCalculatorAddFields.skirting');
            if (stored.found) {
                return stored.value === null || stored.value === '' ? '' : String(stored.value);
            }
            if (inventory.skirting != null && inventory.skirting !== '') return String(inventory.skirting);
            const allowance = getSkirtingAllowanceByType(g('maxSalesPriceCalculatorAddFields.skirtingType', ''), isSingleWide);
            return allowance != null ? String(allowance) : '';
        })(),
        addStepsDecks: g('maxSalesPriceCalculatorAddFields.stepsDecks', stepsPerSetAllowance * numberOfSets),
        addNumberOfSets: g('maxSalesPriceCalculatorAddFields.numberOfSets', ''),
        addDirtPad: g('maxSalesPriceCalculatorAddFields.dirtPad', inventory.dirtPad ?? getAllowanceByItemName('Pad', isSingleWide)) ?? '',
        addFooters: g('maxSalesPriceCalculatorAddFields.footers', inventory.footers ?? getAllowanceByItemName('Footers', isSingleWide)) ?? '',
        addTrimOutTapeAndTexture: g('maxSalesPriceCalculatorAddFields.trimOutTapeAndTexture', getAllowanceByItemName('Trim Out', isSingleWide)) ?? '',
        addElectricHookup: g('maxSalesPriceCalculatorAddFields.electricHookup', getAllowanceByItemName('Electric Hookups', isSingleWide)) ?? '',
        addWaterHookup: g('maxSalesPriceCalculatorAddFields.waterHookup', getAllowanceByItemName('Water Hookups', isSingleWide)) ?? '',
        addSewerHookup: g('maxSalesPriceCalculatorAddFields.sewerHookup', getAllowanceByItemName('Sewer Hookups', isSingleWide)) ?? '',
        addGasHookup: g('maxSalesPriceCalculatorAddFields.gasHookup', getAllowanceByItemName('Gas Hookups', isSingleWide)) ?? '',
        addGuttersAndDownspouts: g('maxSalesPriceCalculatorAddFields.guttersAndDownspouts', inventory.guttersDownspouts ?? getAllowanceByItemName('Gutters & Downspouts', isSingleWide)) ?? '',
        addOther: g('maxSalesPriceCalculatorAddFields.other', inventory.otherAdds) ?? '',
        addOtherAmount: g('maxSalesPriceCalculatorAddFields.otherAmount', ''),

        isSingleWide,

        maxSalesPriceSalesTax: g('maxSalesPriceSalesTax', ''),

        // Land (maxSalesPriceLandPurchasePrice + maxSalesPriceLandImprovements)
        landPurchasePrice: inventory.maxSalesPriceLandPurchasePrice ?? '',
        landBasement: get(inventory, 'maxSalesPriceLandImprovements.basement', '') ?? '',
        landGrading: get(inventory, 'maxSalesPriceLandImprovements.grading', '') ?? '',
        landBrickBlockSkirting: get(inventory, 'maxSalesPriceLandImprovements.brickBlockSkirting', '') ?? '',
        landPermanentFoundation: get(inventory, 'maxSalesPriceLandImprovements.permanentFoundation', '') ?? '',
        landCarport: get(inventory, 'maxSalesPriceLandImprovements.carport', '') ?? '',
        landPermits: get(inventory, 'maxSalesPriceLandImprovements.permits', '') ?? '',
        landDeck: get(inventory, 'maxSalesPriceLandImprovements.deck', '') ?? '',
        landPorch: get(inventory, 'maxSalesPriceLandImprovements.porch', '') ?? '',
        landDriveway: get(inventory, 'maxSalesPriceLandImprovements.driveway', '') ?? '',
        landSeptic: get(inventory, 'maxSalesPriceLandImprovements.septic', '') ?? '',
        landElectricalHookup: get(inventory, 'maxSalesPriceLandImprovements.electricalHookup', '') ?? '',
        landSewerHookup: get(inventory, 'maxSalesPriceLandImprovements.sewerHookup', '') ?? '',
        landFooters: get(inventory, 'maxSalesPriceLandImprovements.footers', '') ?? '',
        landWaterHookup: get(inventory, 'maxSalesPriceLandImprovements.waterHookup', '') ?? '',
        landGarage: get(inventory, 'maxSalesPriceLandImprovements.garage', '') ?? '',
        landWell: get(inventory, 'maxSalesPriceLandImprovements.well', '') ?? '',
        landGasHookup: get(inventory, 'maxSalesPriceLandImprovements.gasHookup', '') ?? '',
        landOther: get(inventory, 'maxSalesPriceLandImprovements.other', '') ?? '',

        // Site of Placement / New-Used section
        // New/Used from inventory.type (or inventoryUnit.type in contract context)
        type: get(inventory, 'type', '') || get(inventory, 'inventoryUnit.type', '') || '',
        // Repo ID from additionalFields.repoId only
        repoId: get(inventory, 'additionalFields.repoId', '') || get(inventory, 'inventoryUnit.additionalFields.repoId', '') || '',
        year: inventory.year || '',
        make: manufacturerName || '',
        model: inventory.modelName || '',
        // Manufacturer and model IDs for dropdowns
        manufacturer: get(inventory, 'manufacturer._id') || get(inventory, 'manufacturer') || get(inventory, 'inventoryUnit.manufacturer._id') || get(inventory, 'inventoryUnit.manufacturer') || '',
        modelId: get(inventory, 'model._id') || get(inventory, 'model') || get(inventory, 'inventoryUnit.model._id') || get(inventory, 'inventoryUnit.model') || '',
        sizeOverall: inventory.sizeOverall || '',
        typeOfUnit: normalizeInventoryTypeOfUnitForPrefill(inventory.typeOfUnit),

        // Basic inventory description
        inventorySummary: manufacturerName || inventory.model?.name || inventory.serialA || ''
    };
};

// Parse a prefill value as number for calculations (ignores commas, etc.)
const parseDecimal = (val) => {
    if (val == null || val === '') return 0;
    const n = parseFloat(String(val).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
};

// Convert prefill value to number for API (undefined if empty)
const toNum = (val) => {
    if (val == null || val === '') return undefined;
    const n = parseDecimal(val);
    return Number.isFinite(n) ? n : undefined;
};

// Persist cleared worksheet fields as null so reload does not re-apply defaults.
const toWorksheetNum = (val) => {
    if (val === '' || val == null) return null;
    const n = parseDecimal(val);
    return Number.isFinite(n) ? n : null;
};

/** Normalize worksheet → Contact API*/
const numOrNull = (v) => {
    if (v === '' || v == null) return null;
    const n = toNum(v);
    return n === undefined ? null : n;
};
const dateOrNull = (v) => {
    if (v === '' || v == null) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};
const labelOrNull = (v) => (v == null || String(v).trim() === '' ? null : String(v).trim());

const isWorksheetBlank = (v) => v == null || v === '';

/** Allowed Inventory.typeOfUnit values for worksheet Home Type (single-select). */
const WORKSHEET_HOME_TYPE_VALUES = ['mfg', 'modular', 'tiny'];
const WORKSHEET_HOME_TYPE_SET = new Set(WORKSHEET_HOME_TYPE_VALUES);

const normalizeInventoryTypeOfUnitForPrefill = (raw) => {
    if (raw == null || String(raw).trim() === '') return '';
    const lower = String(raw).trim().toLowerCase();
    if (WORKSHEET_HOME_TYPE_SET.has(lower)) return lower;
    if (lower.includes('manufactured') || lower === 'mfg') return 'mfg';
    if (lower.includes('modular')) return 'modular';
    if (lower.includes('park') || lower === 'tiny') return 'tiny';
    return '';
};

const WORKSHEET_HOME_TYPE_RADIO_OPTIONS = [
    { value: 'mfg', label: 'Manufactured Home (HUD Code)' },
    { value: 'modular', label: 'Modular Home' },
    { value: 'tiny', label: 'Park Model (ANSI Code)' },
];

/** Site of Placement — UI labels match worksheet; keys match Contact.siteOfPlacementFields */
const SITE_OF_PLACEMENT_CHECKBOXES = [
    { key: 'ownedPropertyFreeAndClear', label: 'Owned Property/Free and Clear' },
    { key: 'leasedLand', label: 'Leased Land' },
    { key: 'ownedPropertyLandContractMortgageTrustDeed', label: 'Owned Property Land Contract/Mortgage Trust Deed' },
    { key: 'familyLand', label: 'Family Land' },
    { key: 'reservation', label: 'Reservation' },
    { key: 'manufacturedHomeCommunity', label: 'Manufactured Home Community' }
];

const SITE_OF_PLACEMENT_DEFAULTS = () => ({
    ownedPropertyFreeAndClear: false,
    leasedLand: false,
    ownedPropertyLandContractMortgageTrustDeed: false,
    familyLand: false,
    reservation: false,
    manufacturedHomeCommunity: false,
    monthlySitePayment: '',
    communityFamilyMortgageHolderReservationName: ''
});

const patchSiteOfPlacementFields = (prev, patch) => ({
    ...prev,
    siteOfPlacementFields: {
        ...SITE_OF_PLACEMENT_DEFAULTS(),
        ...prev.siteOfPlacementFields,
        ...patch,
    },
});
/** Site of placement overlay from Contact or Contract (same nested + root monthly/name shape). */
const mergeSiteOfPlacementFieldsFromCrmRecord = (crmRecord) => {
    const src =
        crmRecord?.siteOfPlacementFields && typeof crmRecord.siteOfPlacementFields === 'object'
            ? crmRecord.siteOfPlacementFields
            : {};
    const monthlyRoot =
        crmRecord?.monthlySitePayment != null && crmRecord.monthlySitePayment !== ''
            ? String(crmRecord.monthlySitePayment)
            : src.monthlySitePayment != null && src.monthlySitePayment !== ''
                ? String(src.monthlySitePayment)
                : '';
    const nameRaw =
        crmRecord?.communityFamilyMortgageHolderReservationName != null &&
            String(crmRecord.communityFamilyMortgageHolderReservationName).trim() !== ''
            ? String(crmRecord.communityFamilyMortgageHolderReservationName).trim()
            : src.communityFamilyMortgageHolderReservationName != null
                ? String(src.communityFamilyMortgageHolderReservationName).trim()
                : '';

    return {
        ownedPropertyFreeAndClear: !!src.ownedPropertyFreeAndClear,
        leasedLand: !!src.leasedLand,
        ownedPropertyLandContractMortgageTrustDeed: !!src.ownedPropertyLandContractMortgageTrustDeed,
        familyLand: !!src.familyLand,
        reservation: !!src.reservation,
        manufacturedHomeCommunity: !!src.manufacturedHomeCommunity,
        monthlySitePayment: monthlyRoot,
        communityFamilyMortgageHolderReservationName: nameRaw
    };
};

/** Checkbox flags only — siteOfPlacementFields nested subdocument (Contact or Contract). */
const buildSiteOfPlacementNestedPayload = (fields) => {
    const s = { ...SITE_OF_PLACEMENT_DEFAULTS(), ...(fields && typeof fields === 'object' ? fields : {}) };
    return {
        ownedPropertyFreeAndClear: !!s.ownedPropertyFreeAndClear,
        leasedLand: !!s.leasedLand,
        ownedPropertyLandContractMortgageTrustDeed: !!s.ownedPropertyLandContractMortgageTrustDeed,
        familyLand: !!s.familyLand,
        reservation: !!s.reservation,
        manufacturedHomeCommunity: !!s.manufacturedHomeCommunity,
    };
};

/** Root CRM fields (Contact or Contract) — not nested under siteOfPlacementFields. */
const buildMonthlySitePaymentRootPayload = (fields) => {
    const s = { ...SITE_OF_PLACEMENT_DEFAULTS(), ...(fields && typeof fields === 'object' ? fields : {}) };
    return {
        monthlySitePayment: numOrNull(s.monthlySitePayment),
        communityFamilyMortgageHolderReservationName: labelOrNull(s.communityFamilyMortgageHolderReservationName),
    };
};

/** Checkbox labels — stored on amountDesiredCalculatorFields.SourcesOfCashDownPayment */
const SOURCES_OF_CASH_DOWN_PAYMENT_LABELS = ['Checking', 'Savings', 'Cash on Hand', 'Loan', 'Gift'];

/** Overlay Amount Desired rows from Contact or Contract (prefill keys stay inventory-shaped). */
const mergeAmountDesiredFieldsFromCrmRecord = (crmRecord) => {
    if (!crmRecord) return {};
    const adc = crmRecord.amountDesiredCalculatorFields || {};
    const out = {};
    if (crmRecord.salesPrice != null) {
        out.salesPrice = String(crmRecord.salesPrice);
    }
    if (adc.salesTax != null && adc.salesTax !== '') {
        out.amountDesiredSalesTax = String(adc.salesTax);
    }
    if (adc.landImprovements != null) {
        out.amountDesiredLandImprovements = String(adc.landImprovements);
    }
    if (adc.landPurchasePrice != null) {
        out.landPurchasePrice = String(adc.landPurchasePrice);
    }
    if (crmRecord.tradeInGrossAllowance != null) {
        out.tradeInGrossAllowance = String(crmRecord.tradeInGrossAllowance);
    }
    if (crmRecord.lessBalanceDue != null) {
        out.lessBalanceDue = String(crmRecord.lessBalanceDue);
    }
    if (crmRecord.downPayment != null) {
        out.downPayment = String(crmRecord.downPayment);
    }
    if (crmRecord.insuranceCosts != null) {
        out.insuranceCosts = String(crmRecord.insuranceCosts);
    }

    const rawSources = adc.SourcesOfCashDownPayment;
    let sources = [];
    if (Array.isArray(rawSources)) {
        sources = rawSources.filter(x => x != null && String(x).trim() !== '').map(String);
    } else if (rawSources != null && rawSources !== '') {
        sources = [String(rawSources)];
    }
    out.sourcesOfCashDownPayment = sources;
    out.sourcesOfCashDownPaymentFromWhom =
        adc.sourcesOfCashDownPaymentFromWhom != null ? String(adc.sourcesOfCashDownPaymentFromWhom) : '';

    return out;
};

const formatContactDateForInput = (d) => {
    if (d == null || d === '') return '';
    if (typeof d === 'string') return d.split('T')[0];
    try {
        const x = new Date(d);
        if (Number.isNaN(x.getTime())) return '';
        return x.toISOString().split('T')[0];
    } catch {
        return '';
    }
};

/** LAND in LIEU — overlay from lieuEquityCalculatorFields */
const mergeLieuEquityFieldsFromCrmRecord = (crmRecord) => {
    if (!crmRecord) return {};
    const lec = crmRecord.lieuEquityCalculatorFields;
    if (!lec || typeof lec !== 'object') return {};
    const out = {};
    if (lec.landSizeInAcres != null && lec.landSizeInAcres !== '') {
        out.lieuLandSizeInAcres = String(lec.landSizeInAcres);
    }
    const dp = formatContactDateForInput(lec.datePurchased);
    if (dp !== '') out.lieuDatePurchased = dp;
    if (lec.landMarketValue != null && lec.landMarketValue !== '') {
        out.lieuLandMarketValue = String(lec.landMarketValue);
    }
    if (lec.lessLandPayoff != null && lec.lessLandPayoff !== '') {
        out.lieuLessLandPayoff = String(lec.lessLandPayoff);
    }
    return out;
};

/**  subsection — amountDesiredCalculatorAddFields ↔ worksheet keys ([apiField, prefillKey]). */
const ADC_ADD_NUMERIC_CONTACT_KEYS = [
    ['freight', 'adcAddFreight'],
    ['airConditioner', 'adcAddAirConditioner'],
    ['HUDDuesFees', 'adcAddHUDDuesFees'],
    ['deliveryAndSet', 'adcAddDeliveryAndSet'],
    ['skirting', 'adcAddSkirting'],
    ['stepsDecks', 'adcAddStepsDecks'],
    ['dirtPad', 'adcAddDirtPad'],
    ['trimOutTapeAndTexture', 'adcMspTrimOutTapeAndTexture'],
    ['electricHookup', 'adcMspElectricHookup'],
    ['waterHookup', 'adcMspWaterHookup'],
    ['sewerHookup', 'adcMspSewerHookup'],
    ['otherAmount1', 'otherAmount1'],
    ['otherAmount2', 'otherAmount2'],
    ['otherAmount3', 'otherAmount3'],
];
const ADC_ADD_LABEL_CONTACT_KEYS = [
    ['otherLabel1', 'otherLabel1'],
    ['otherLabel2', 'otherLabel2'],
    ['otherLabel3', 'otherLabel3'],
];

/** Legacy nested keys on Contact.amountDesiredCalculatorAddFields — migrated reads only; stripped on persist. */
const LEGACY_AMOUNT_DESIRED_ADD_OTHER_NUMERIC = [
    ['mspOther1Amount', 'otherAmount1'],
    ['mspOther2Amount', 'otherAmount2'],
    ['mspOther3Amount', 'otherAmount3'],
];
const LEGACY_AMOUNT_DESIRED_ADD_OTHER_LABEL = [
    ['mspOther1Label', 'otherLabel1'],
    ['mspOther2Label', 'otherLabel2'],
    ['mspOther3Label', 'otherLabel3'],
];
const LEGACY_AMOUNT_DESIRED_ADD_OTHER_KEYS = [
    'mspOther1Label',
    'mspOther1Amount',
    'mspOther2Label',
    'mspOther2Amount',
    'mspOther3Label',
    'mspOther3Amount',
];

const mergeAmountDesiredCalculatorAddFieldsFromCrmRecord = (crmRecord) => {
    if (!crmRecord) return {};
    const a = crmRecord.amountDesiredCalculatorAddFields;
    if (!a || typeof a !== 'object') return {};
    const out = {};
    for (const [api, pref] of ADC_ADD_NUMERIC_CONTACT_KEYS) {
        if (a[api] != null && a[api] !== '') out[pref] = String(a[api]);
    }
    for (const [api, pref] of ADC_ADD_LABEL_CONTACT_KEYS) {
        if (a[api] != null) out[pref] = String(a[api]);
    }
    for (const [legacyApi, pref] of LEGACY_AMOUNT_DESIRED_ADD_OTHER_NUMERIC) {
        if (!(pref in out) && a[legacyApi] != null && a[legacyApi] !== '') {
            out[pref] = String(a[legacyApi]);
        }
    }
    for (const [legacyApi, pref] of LEGACY_AMOUNT_DESIRED_ADD_OTHER_LABEL) {
        if (!(pref in out) && a[legacyApi] != null) {
            out[pref] = String(a[legacyApi]);
        }
    }
    return out;
};

/** USED HOME / 21st REPO MSP — markup (A) from twentyFirstRepoMspCalculatorFields.totalMarkup */
const mergeTwentyFirstRepoMspFromCrmRecord = (crmRecord) => {
    if (!crmRecord) return {};
    const r = crmRecord.twentyFirstRepoMspCalculatorFields;
    if (!r || typeof r !== 'object') return {};
    const out = {};
    if (r.totalMarkup != null && r.totalMarkup !== '') {
        out.mspRepoMarkupA = String(r.totalMarkup);
    }
    return out;
};

const mergeTradeAdditionalFieldsFromCrmRecord = (crmRecord) => {
    if (!crmRecord?.additionalFields || typeof crmRecord.additionalFields !== 'object') return {};
    const af = crmRecord.additionalFields;
    const out = {};
    if (af.tradeYear != null && String(af.tradeYear).trim() !== '') out.tradeYear = String(af.tradeYear).trim();
    if (af.tradeMakeModel != null && String(af.tradeMakeModel).trim() !== '')
        out.tradeMakeModel = String(af.tradeMakeModel).trim();
    if (af.tradeSize != null && String(af.tradeSize).trim() !== '') out.tradeSize = String(af.tradeSize).trim();
    return out;
};

const buildCrmAdditionalFieldsTradePersistPayload = (prefill, crmRecord) => {
    const prev =
        crmRecord?.additionalFields &&
            typeof crmRecord.additionalFields === 'object' &&
            !Array.isArray(crmRecord.additionalFields)
            ? { ...crmRecord.additionalFields }
            : {};
    const setOrDel = (key, val) => {
        const s = val != null ? String(val).trim() : '';
        if (s === '') delete prev[key];
        else prev[key] = s;
    };
    setOrDel('tradeYear', prefill.tradeYear);
    setOrDel('tradeMakeModel', prefill.tradeMakeModel);
    setOrDel('tradeSize', prefill.tradeSize);
    return { additionalFields: prev };
};

/** Persist Amount Desired worksheet edits onto Contact or Contract (same nested shape). */
const buildAmountDesiredCrmPersistPayload = (prefill, crmRecord) => {
    const prevAdc =
        crmRecord?.amountDesiredCalculatorFields && typeof crmRecord.amountDesiredCalculatorFields === 'object'
            ? { ...crmRecord.amountDesiredCalculatorFields }
            : {};
    const prevAdds =
        crmRecord?.amountDesiredCalculatorAddFields && typeof crmRecord.amountDesiredCalculatorAddFields === 'object'
            ? { ...crmRecord.amountDesiredCalculatorAddFields }
            : {};
    const prevLieu =
        crmRecord?.lieuEquityCalculatorFields && typeof crmRecord.lieuEquityCalculatorFields === 'object'
            ? { ...crmRecord.lieuEquityCalculatorFields }
            : {};
    const prevRepo =
        crmRecord?.twentyFirstRepoMspCalculatorFields && typeof crmRecord.twentyFirstRepoMspCalculatorFields === 'object'
            ? { ...crmRecord.twentyFirstRepoMspCalculatorFields }
            : {};
    return {
        salesPrice: numOrNull(prefill.salesPrice),
        downPayment: numOrNull(prefill.downPayment),
        insuranceCosts: numOrNull(prefill.insuranceCosts),
        tradeInGrossAllowance: numOrNull(prefill.tradeInGrossAllowance),
        lessBalanceDue: numOrNull(prefill.lessBalanceDue),
        amountDesiredCalculatorFields: {
            ...prevAdc,
            salesTax: numOrNull(prefill.amountDesiredSalesTax),
            landImprovements: numOrNull(prefill.amountDesiredLandImprovements),
            landPurchasePrice: numOrNull(prefill.landPurchasePrice),
            SourcesOfCashDownPayment: Array.isArray(prefill.sourcesOfCashDownPayment)
                ? prefill.sourcesOfCashDownPayment
                    .filter(x => x != null && String(x).trim() !== '')
                    .map(String)
                : [],
            sourcesOfCashDownPaymentFromWhom:
                prefill.sourcesOfCashDownPaymentFromWhom != null &&
                    String(prefill.sourcesOfCashDownPaymentFromWhom).trim() !== ''
                    ? String(prefill.sourcesOfCashDownPaymentFromWhom).trim()
                    : null,
        },
        lieuEquityCalculatorFields: (() => {
            const lieu = { ...prevLieu };
            delete lieu.totalLandEquity;
            return {
                ...lieu,
                landSizeInAcres: numOrNull(prefill.lieuLandSizeInAcres),
                datePurchased: dateOrNull(prefill.lieuDatePurchased),
                landMarketValue: numOrNull(prefill.lieuLandMarketValue),
                lessLandPayoff: numOrNull(prefill.lieuLessLandPayoff),
            };
        })(),
        amountDesiredCalculatorAddFields: (() => {
            const adds = {
                ...prevAdds,
                ...Object.fromEntries(ADC_ADD_NUMERIC_CONTACT_KEYS.map(([api, pref]) => [api, numOrNull(prefill[pref])])),
                ...Object.fromEntries(ADC_ADD_LABEL_CONTACT_KEYS.map(([api, pref]) => [api, labelOrNull(prefill[pref])])),
            };
            for (const k of LEGACY_AMOUNT_DESIRED_ADD_OTHER_KEYS) delete adds[k];
            return adds;
        })(),
        twentyFirstRepoMspCalculatorFields: {
            ...prevRepo,
            totalMarkup: numOrNull(prefill.mspRepoMarkupA),
        },
    };
};

/** Derived read-only lines for CALCULATION FOR AMOUNT DESIRED (rows 3, 6, 9, 10, 12). */
const computeAmountDesiredDerived = (prefill) => {
    const row3Total =
        isWorksheetBlank(prefill.salesPrice) && isWorksheetBlank(prefill.amountDesiredSalesTax)
            ? null
            : parseDecimal(prefill.salesPrice) + parseDecimal(prefill.amountDesiredSalesTax);
    const row6Total =
        isWorksheetBlank(prefill.salesPrice) &&
            isWorksheetBlank(prefill.amountDesiredSalesTax) &&
            isWorksheetBlank(prefill.amountDesiredLandImprovements) &&
            isWorksheetBlank(prefill.landPurchasePrice)
            ? null
            : parseDecimal(prefill.salesPrice) +
            parseDecimal(prefill.amountDesiredSalesTax) +
            parseDecimal(prefill.amountDesiredLandImprovements) +
            parseDecimal(prefill.landPurchasePrice);

    const g = prefill.tradeInGrossAllowance;
    const l = prefill.lessBalanceDue;
    let netTradeComp = null;
    if (!((g == null || g === '') && (l == null || l === ''))) {
        netTradeComp = parseDecimal(g) - parseDecimal(l);
    }
    const row9Total =
        netTradeComp === null && isWorksheetBlank(prefill.downPayment)
            ? null
            : (netTradeComp ?? 0) + parseDecimal(prefill.downPayment);

    const row10Total =
        row6Total === null || row9Total === null ? null : row6Total - row9Total;

    let row12Total = null;
    const insBlank = isWorksheetBlank(prefill.insuranceCosts);
    if (row10Total !== null || !insBlank) {
        row12Total = (row10Total ?? 0) + parseDecimal(prefill.insuranceCosts);
    }

    return {
        row3: row3Total === null ? '' : String(row3Total),
        row6: row6Total === null ? '' : String(row6Total),
        row9: row9Total === null ? '' : String(row9Total),
        row10: row10Total === null ? '' : String(row10Total),
        row12: row12Total === null ? '' : String(row12Total)
    };
};

// Build inventory payload from worksheet prefill for PUT (matches backend inventoryModel)
const buildWorksheetPayload = (prefill) => {
    if (!prefill) return {};
    return {
        salesPrice: toWorksheetNum(prefill.salesPrice),
        stateTax: toWorksheetNum(prefill.stateTax),
        tradeInGrossAllowance: toWorksheetNum(prefill.tradeInGrossAllowance),
        lessBalanceDue: toWorksheetNum(prefill.lessBalanceDue),
        downPayment: toWorksheetNum(prefill.downPayment),
        insuranceCosts: toWorksheetNum(prefill.insuranceCosts),
        amountDesiredLandImprovements: toWorksheetNum(prefill.amountDesiredLandImprovements),
        invoice: toWorksheetNum(prefill.invoice),
        maxSalesPriceCalculatorDeletedFields: {
            wheelsAndAxles: toWorksheetNum(prefill.wheelsAndAxles),
            factoryFreight: toWorksheetNum(prefill.freight),
            airConditioner: toWorksheetNum(prefill.airConditioner),
            hudDuesFees: toWorksheetNum(prefill.hudDuesFees),
            furnitureEntertainmentPack: toWorksheetNum(prefill.furnitureEntertainmentPack),
            otherPacksSalesAllowances: toWorksheetNum(prefill.otherPacksSalesAllowances),
            dealerRebate: toWorksheetNum(prefill.dealerRebate),
            advertisingEnvironmentalAllowance: toWorksheetNum(prefill.advertisingEnvironmentalAllowance),
            trimOutTapeAndTexture: toWorksheetNum(prefill.trimOutTapeAndTexture),
            other: toWorksheetNum(prefill.otherDeletion),
            taxFromInvoice: toWorksheetNum(prefill.taxFromInvoice),
        },
        maxSalesPriceCalculatorAddFields: {
            factoryFreight: toWorksheetNum(prefill.addFactoryFreight),
            airConditioner: toWorksheetNum(prefill.addAirConditioner),
            hudDuesFees: toWorksheetNum(prefill.addHudDuesFees),
            deliveryAndSet: toWorksheetNum(prefill.addDeliveryAndSet),
            skirting: toWorksheetNum(prefill.addSkirting),
            skirtingType: prefill.addSkirtingType != null && prefill.addSkirtingType !== '' ? String(prefill.addSkirtingType) : null,
            stepsDecks: toWorksheetNum(prefill.addStepsDecks),
            numberOfSets: toWorksheetNum(prefill.addNumberOfSets),
            dirtPad: toWorksheetNum(prefill.addDirtPad),
            footers: toWorksheetNum(prefill.addFooters),
            trimOutTapeAndTexture: toWorksheetNum(prefill.addTrimOutTapeAndTexture),
            electricHookup: toWorksheetNum(prefill.addElectricHookup),
            waterHookup: toWorksheetNum(prefill.addWaterHookup),
            sewerHookup: toWorksheetNum(prefill.addSewerHookup),
            gasHookup: toWorksheetNum(prefill.addGasHookup),
            guttersAndDownspouts: toWorksheetNum(prefill.addGuttersAndDownspouts),
            other: prefill.addOther != null && prefill.addOther !== '' ? String(prefill.addOther) : null,
            otherAmount: toWorksheetNum(prefill.addOtherAmount),
            taxFromInvoice: toWorksheetNum(prefill.addTaxFromInvoice),
        },
        maxSalesPriceSalesTax: toWorksheetNum(prefill.maxSalesPriceSalesTax),
        maxSalesPriceLandPurchasePrice: toWorksheetNum(prefill.landPurchasePrice),
        maxSalesPriceLandImprovements: {
            basement: toWorksheetNum(prefill.landBasement),
            grading: toWorksheetNum(prefill.landGrading),
            brickBlockSkirting: toWorksheetNum(prefill.landBrickBlockSkirting),
            permanentFoundation: toWorksheetNum(prefill.landPermanentFoundation),
            carport: toWorksheetNum(prefill.landCarport),
            permits: toWorksheetNum(prefill.landPermits),
            deck: toWorksheetNum(prefill.landDeck),
            porch: toWorksheetNum(prefill.landPorch),
            driveway: toWorksheetNum(prefill.landDriveway),
            septic: toWorksheetNum(prefill.landSeptic),
            electricalHookup: toWorksheetNum(prefill.landElectricalHookup),
            sewerHookup: toWorksheetNum(prefill.landSewerHookup),
            footers: toWorksheetNum(prefill.landFooters),
            waterHookup: toWorksheetNum(prefill.landWaterHookup),
            garage: toWorksheetNum(prefill.landGarage),
            well: toWorksheetNum(prefill.landWell),
            gasHookup: toWorksheetNum(prefill.landGasHookup),
            other: toWorksheetNum(prefill.landOther),
        },
        typeOfUnit: (() => {
            const t =
                prefill.typeOfUnit != null && String(prefill.typeOfUnit).trim() !== ''
                    ? String(prefill.typeOfUnit).trim().toLowerCase()
                    : '';
            return WORKSHEET_HOME_TYPE_SET.has(t) ? t : undefined;
        })(),
    };
};

// Generic input component that enforces editability rules
const WorksheetInput = ({ value, onChange, isInventoryField, className = "ws-input", ...rest }) => {
    // Inventory fields are editable controlled inputs
    if (isInventoryField) {
        return (
            <input
                type="text"
                className={className}
                value={value ?? ''}
                onChange={e => onChange(e.target.value)}
                {...rest}
            />
        );
    }

    // Non-inventory fields are read-only; keep uncontrolled to avoid warnings
    return <input type="text" className={className} readOnly />;
};

/** Single worksheet cell bound to `prefill[fieldKey]` (editable rows). */
const WorksheetPrefillTd = ({ prefill, setPrefill, fieldKey, inputClass = 'ws-input w-10 inline', placeholder }) => (
    <td className="ws-cell">
        <WorksheetInput
            className={inputClass}
            value={prefill[fieldKey] ?? ''}
            onChange={val => setPrefill(prev => ({ ...prev, [fieldKey]: val }))}
            isInventoryField
            placeholder={placeholder}
        />
    </td>
);

const ReadOnlyWorksheetValue = ({ value, className = 'ws-input w-24 inline', tabIndex }) => (
    <input type="text" className={className} readOnly value={value ?? ''} tabIndex={tabIndex} />
);

/** MSP ADDS grid row defs — dual columns or dual + other (label + amount). */
const MSP_ADD_DUAL_ROWS = [
    ['Freight:', 'adcAddFreight', 'Trim Out/Tape & Texture:', 'adcMspTrimOutTapeAndTexture'],
    ['Air Conditioner:', 'adcAddAirConditioner', 'Electric Hookup:', 'adcMspElectricHookup'],
    ['HUD Dues/Fees:', 'adcAddHUDDuesFees', 'Water Hookup:', 'adcMspWaterHookup'],
    ['Delivery & Set:', 'adcAddDeliveryAndSet', 'Sewer Hookup:', 'adcMspSewerHookup'],
];
const MSP_ADD_OTHER_ROWS = [
    ['Skirting:', 'adcAddSkirting', 'otherLabel1', 'otherAmount1'],
    ['Steps/Decks:', 'adcAddStepsDecks', 'otherLabel2', 'otherAmount2'],
    ['Dirt Pad/Footers:', 'adcAddDirtPad', 'otherLabel3', 'otherAmount3'],
];
const MSP_ADD_AMOUNT_KEYS = ADC_ADD_NUMERIC_CONTACT_KEYS.map(([, p]) => p);

// CalculationAmountDesired Component
const CalculationAmountDesired = ({ prefill, setPrefill, tradeFieldsEditable = false }) => {
    const derived = computeAmountDesiredDerived(prefill);
    return (
        <div className="border border-ws-cell-border">
            <div className="ws-header ws-red text-sm">CALCULATION FOR AMOUNT DESIRED</div>

            {/* Line items */}
            <table className="w-full text-xs border-collapse">
                <tbody>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">1. HOME Sales Price: (including adds)</td>
                        <td className="ws-cell w-8 text-right">$</td>
                        <td className="ws-cell w-24">
                            <WorksheetInput
                                value={prefill.salesPrice || ''}
                                onChange={val => setPrefill(prev => ({ ...prev, salesPrice: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">2. Sales Tax:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.amountDesiredSalesTax ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, amountDesiredSalesTax: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">3. TOTAL HOME Sales Price: (1+2)</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <input type="text" className="ws-input" readOnly value={derived.row3} />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">4. Land Improvements:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.amountDesiredLandImprovements ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, amountDesiredLandImprovements: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">5. Land Purchase Price / Payoff:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.landPurchasePrice ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, landPurchasePrice: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">6. Total Package Price: (total of 3+4+5)</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <input type="text" className="ws-input" readOnly value={derived.row6} />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">7. &nbsp;&nbsp;a. Gross Trade-In:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.tradeInGrossAllowance || ''}
                                onChange={val => setPrefill(prev => ({ ...prev, tradeInGrossAllowance: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;b.Less Amount. Owed On Trade-In:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.lessBalanceDue ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, lessBalanceDue: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;c.Net Trade-In: (7a minus 7b)</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <input
                                type="text"
                                className="ws-input"
                                readOnly
                                value={(() => {
                                    const g = prefill.tradeInGrossAllowance;
                                    const l = prefill.lessBalanceDue;
                                    if ((g == null || g === '') && (l == null || l === '')) return '';
                                    const net = parseDecimal(g) - parseDecimal(l);
                                    return String(net);
                                })()}
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td colSpan={3} className="ws-cell">
                            <div className="flex items-center gap-2 text-xs flex-wrap">
                                <span className="ws-bold">Trade Year:</span>
                                <WorksheetInput
                                    className="ws-input-box w-14"
                                    value={prefill.tradeYear ?? ''}
                                    onChange={val => setPrefill(prev => ({ ...prev, tradeYear: val }))}
                                    isInventoryField={tradeFieldsEditable}
                                />
                                <span className="ws-bold">Make/Model:</span>
                                <WorksheetInput
                                    className="ws-input-box flex-1 min-w-0"
                                    value={prefill.tradeMakeModel ?? ''}
                                    onChange={val => setPrefill(prev => ({ ...prev, tradeMakeModel: val }))}
                                    isInventoryField={tradeFieldsEditable}
                                />
                                <span className="ws-bold">Width x Length:</span>
                                <WorksheetInput
                                    className="ws-input-box w-24"
                                    value={prefill.tradeSize ?? ''}
                                    onChange={val => setPrefill(prev => ({ ...prev, tradeSize: val }))}
                                    isInventoryField={tradeFieldsEditable}
                                />
                            </div>
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">8.Cash Down Payment:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.downPayment || ''}
                                onChange={val => setPrefill(prev => ({ ...prev, downPayment: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td colSpan={3} className="ws-cell">
                            <div className="text-xs mb-1">Sources of Cash Down Payment:</div>
                            <div className="flex gap-3 text-xs flex-wrap">
                                {SOURCES_OF_CASH_DOWN_PAYMENT_LABELS.slice(0, 3).map(label => (
                                    <label key={label} className="flex items-center gap-1">
                                        <input
                                            type="checkbox"
                                            className="ws-checkbox"
                                            checked={(prefill.sourcesOfCashDownPayment || []).includes(label)}
                                            onChange={e => {
                                                const checked = e.currentTarget.checked;
                                                setPrefill(prev => {
                                                    const cur = Array.isArray(prev.sourcesOfCashDownPayment)
                                                        ? [...prev.sourcesOfCashDownPayment]
                                                        : [];
                                                    if (checked) {
                                                        if (!cur.includes(label)) cur.push(label);
                                                    } else {
                                                        const ix = cur.indexOf(label);
                                                        if (ix !== -1) cur.splice(ix, 1);
                                                    }
                                                    return { ...prev, sourcesOfCashDownPayment: cur };
                                                });
                                            }}
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>
                            <div className="flex gap-3 text-xs mt-1 flex-wrap items-center">
                                {SOURCES_OF_CASH_DOWN_PAYMENT_LABELS.slice(3).map(label => (
                                    <label key={label} className="flex items-center gap-1">
                                        <input
                                            type="checkbox"
                                            className="ws-checkbox"
                                            checked={(prefill.sourcesOfCashDownPayment || []).includes(label)}
                                            onChange={e => {
                                                const checked = e.currentTarget.checked;
                                                setPrefill(prev => {
                                                    const cur = Array.isArray(prev.sourcesOfCashDownPayment)
                                                        ? [...prev.sourcesOfCashDownPayment]
                                                        : [];
                                                    if (checked) {
                                                        if (!cur.includes(label)) cur.push(label);
                                                    } else {
                                                        const ix = cur.indexOf(label);
                                                        if (ix !== -1) cur.splice(ix, 1);
                                                    }
                                                    return { ...prev, sourcesOfCashDownPayment: cur };
                                                });
                                            }}
                                        />
                                        {label}
                                    </label>
                                ))}
                                <span>From Whom:</span>
                                <WorksheetInput
                                    value={prefill.sourcesOfCashDownPaymentFromWhom ?? ''}
                                    onChange={val =>
                                        setPrefill(prev => ({ ...prev, sourcesOfCashDownPaymentFromWhom: val }))
                                    }
                                    isInventoryField
                                    className="ws-input flex-1 min-w-[8rem]"
                                />
                            </div>
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">9.TOTAL Down Payment: (7c+8)</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <input type="text" className="ws-input" readOnly value={derived.row9} />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">10.Unpaid Balance of Total Package: (6 minus 9)</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <input type="text" className="ws-input" readOnly value={derived.row10} />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">
                            11.Physical Damage Insurance Premium:
                            <div className="text-[8px] text-muted-foreground italic">(1 Yr Insurance Required to be financed or paid in full)</div>
                        </td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.insuranceCosts || ''}
                                onChange={val => setPrefill(prev => ({ ...prev, insuranceCosts: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">12.Requested Loan Amount: (10+11)</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <input type="text" className="ws-input" readOnly value={derived.row12} />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

/** C.Total Land Equity = A.Land Market Value − B.Less Land Payoff (derived; not persisted). */
const computeLieuTotalLandEquityDisplay = (landMarketValue, lessLandPayoff) => {
    const a = toNum(landMarketValue);
    if (a === undefined) return '';
    const b = toNum(lessLandPayoff);
    const payoff = b === undefined ? 0 : b;
    return String(a - payoff);
};

// LandEquity Component — Contact.lieuEquityCalculatorFields
const LandEquity = ({ prefill, setPrefill }) => {
    const lieuTotalLandEquityDisplay = computeLieuTotalLandEquityDisplay(
        prefill.lieuLandMarketValue,
        prefill.lieuLessLandPayoff
    );
    return (
        <div className="border border-ws-cell-border ">
            <div className="ws-header text-sm" >LAND in LIEU Equity Calculation</div>
            <table className="w-full text-xs border-collapse">
                <tbody>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Land Size:</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.lieuLandSizeInAcres ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, lieuLandSizeInAcres: val }))}
                                isInventoryField
                                placeholder="Acres"
                            />
                        </td>
                        <td className="ws-cell">Date Purchased:</td>
                        <td className="ws-cell">
                            <input
                                type="date"
                                className="ws-input"
                                value={prefill.lieuDatePurchased ?? ''}
                                onChange={e => {
                                    const v = e.currentTarget.value;
                                    setPrefill(prev => ({ ...prev, lieuDatePurchased: v }));
                                }}
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">A.Land Market Value:</td>
                        <td className="ws-cell" colSpan={2}></td>
                        <td className="ws-cell">
                            $ <WorksheetInput
                                className="ws-input w-20 inline"
                                value={prefill.lieuLandMarketValue ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, lieuLandMarketValue: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">B.Less Land Payoff (if applicable):</td>
                        <td className="ws-cell" colSpan={2}></td>
                        <td className="ws-cell">
                            $ <WorksheetInput
                                className="ws-input w-20 inline"
                                value={prefill.lieuLessLandPayoff ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, lieuLessLandPayoff: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">C.Total Land Equity:</td>
                        <td className="ws-cell" colSpan={2}></td>
                        <td className="ws-cell ws-bold">
                            $ <ReadOnlyWorksheetValue className="ws-input w-20 inline" value={lieuTotalLandEquityDisplay} />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

// Deletion row keys in order (for editable rows and for Total Deletions sum)
const DELETION_KEYS = [
    'wheelsAndAxles',
    'freight',
    'airConditioner',
    'taxFromInvoice',
    'hudDuesFees',
    'furnitureEntertainmentPack',
    'otherPacksSalesAllowances',
    'dealerRebate',
    'advertisingEnvironmentalAllowance',
    'trimOutTapeAndTexture',
    'otherDeletion',
];
const DELETION_LABELS = [
    'Wheels & Axles: ($1000 DW, $500 SW)',
    'Factory Freight:',
    'Air Conditioner:',
    'Tax from Invoice:',
    'HUD Dues/Fees:',
    'Furniture/Entertainment Pack:',
    'Other Packs/Sales Allowances:',
    'Dealer Rebate:',
    'Advertising/Environmental Allowance:',
    'Trim Out/Tape & Texture:',
    'Other:',
];

// Add fields that are dollar amounts (for Total Adds sum)
const ADD_AMOUNT_KEYS = [
    'addFactoryFreight', 'addAirConditioner', 'addTaxFromInvoice', 'addHudDuesFees',
    'addDeliveryAndSet', 'addSkirting', 'addStepsDecks', 'addDirtPad', 'addFooters',
    'addTrimOutTapeAndTexture', 'addElectricHookup', 'addWaterHookup', 'addSewerHookup',
    'addGasHookup', 'addGuttersAndDownspouts', 'addOtherAmount',
];

// 1. UnitMaxSalesPrice — Manufacturer's Invoice through Maximum Sales Price of Home (I)
// VEP code → adjustment in percentage points (e.g. "0" → +5, "1" → 0, "2" → -5)
const getVepAdjustmentPoints = (vepCode) => {
    const v = String(vepCode ?? '').trim();
    if (v === '0') return 5;
    if (v === '2') return -5;
    return 0; // "1" or anything else
};

const UnitMaxSalesPrice = ({ prefill, setPrefill }) => {
    const totalDeletions = DELETION_KEYS.reduce((sum, key) => sum + parseDecimal(prefill[key]), 0);
    const invoiceAmount = parseDecimal(prefill.invoice);
    const newHomeNetInvoice = invoiceAmount - totalDeletions;

    const vepAdjustmentPoints = getVepAdjustmentPoints(prefill.vepCode);
    const adjustedMarkupPct = 145 + vepAdjustmentPoints;
    const adjustedMarkupAmount = newHomeNetInvoice * (adjustedMarkupPct / 100);

    const vepAdjustmentDisplay = vepAdjustmentPoints > 0 ? `+${vepAdjustmentPoints}%` : vepAdjustmentPoints < 0 ? `${vepAdjustmentPoints}%` : '0';

    const totalAdds = ADD_AMOUNT_KEYS.reduce((sum, key) => sum + parseDecimal(prefill[key]), 0);
    const totalHomeValue = adjustedMarkupAmount + totalAdds;
    const salesTaxNotOnInvoice = parseDecimal(prefill.maxSalesPriceSalesTax);
    const maximumSalesPriceOfHome = totalHomeValue + salesTaxNotOnInvoice;

    return (
        <div className="border border-ws-cell-border">
            <div className="ws-header ws-red text-sm">CALCULATION FOR MAXIMUM SALES PRICE (NEW Home)</div>

            <div className="grid grid-cols-2 gap-0">
                <div className="border-r border-ws-cell-border">
                    <div className="ws-cell ws-bold border-b border-ws-cell-border">Manufacturer's Invoice: (A)</div>
                </div>
                <div>
                    <div className="ws-cell ws-bold text-center border-b border-ws-cell-border">
                        <WorksheetInput
                            value={prefill.invoice || ''}
                            onChange={val => setPrefill(prev => ({ ...prev, invoice: val }))}
                            isInventoryField
                        />
                    </div>
                </div>
            </div>

            <div className="ws-blue text-sm font-bold">DELETIONS: (from Manufacturer's Invoice)</div>
            <table className="w-full text-xs border-collapse">
                <tbody>
                    {DELETION_LABELS.map((label, i) => {
                        const key = DELETION_KEYS[i];
                        return (
                            <tr key={key} className="border-b border-ws-cell-border">
                                <td className="ws-cell">{label}</td>
                                <td className="ws-cell w-8 text-right">$</td>
                                <td className="ws-cell w-24">
                                    <WorksheetInput
                                        value={prefill[key] ?? ''}
                                        onChange={val => setPrefill(prev => ({ ...prev, [key]: val }))}
                                        isInventoryField
                                    />
                                </td>
                            </tr>
                        );
                    })}
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">Total Deletions</td>
                        <td className="ws-cell ws-bold text-right">(B)</td>
                        <td className="ws-cell ws-bold"><input type="text" className="ws-input w-16 inline" readOnly value={totalDeletions} /></td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">New Home Net Invoice: &nbsp;&nbsp;( A-B= C )</td>
                        <td className="ws-cell ws-bold text-right">(C)</td>
                        <td className="ws-cell ws-bold"><input type="text" className="ws-input w-12 inline" readOnly value={newHomeNetInvoice} /></td>
                    </tr>
                </tbody>
            </table>

            <div className="ws-cell ws-bold  flex items-center gap-2">
                <span className="ws-blue ws-bold text-lg">Markup:</span>
                <span>145 % New & Model 4 years old or less</span>
                <span className="ml-auto">145%</span>
            </div>

            <table className="w-full text-xs border-collapse">
                <tbody>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell ws-bold ws-blue align-top">
                            <div>VEP CODE Adjustments:</div>
                            <div className="ws-font-normal mt-0.5 text-center ws-black">if &quot;0&quot; add 5%</div>
                            <div className="ws-font-normal text-center ws-black">if &quot;1&quot; no adjustment</div>
                            <div className="ws-font-normal text-center ws-black">if &quot;2&quot; subtract 5%</div>
                        </td>
                        <td className="ws-cell w-8 text-right"></td>
                        <td className="ws-cell align-top">
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="ws-bold whitespace-nowrap ws-black">VEP Adjustment</span>
                                <div className="w-10 shrink-0">
                                    <select
                                        className="form-select form-select-sm ws-input w-full max-w-full"
                                        style={{ width: '40px', minWidth: '40px' }}
                                        value={prefill.vepCode ?? ''}
                                        onChange={e => { const v = e.target.value; setPrefill(prev => ({ ...prev, vepCode: v })); }}
                                    >
                                        <option value="0">0</option>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mt-0.5 ws-black">{vepAdjustmentDisplay}</div>
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">Adjusted Markup %</td>
                        <td className="ws-cell ws-bold text-right">(D)</td>
                        <td className="ws-cell"><input type="text" className="ws-input" readOnly value={`${adjustedMarkupPct}%`} /></td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">Adjusted Markup Amount &nbsp;&nbsp;&nbsp;(CxD=E)</td>
                        <td className="ws-cell ws-bold">(E)</td>
                        <td className="ws-cell"><input type="text" className="ws-input" readOnly value={adjustedMarkupAmount.toFixed(2)} /></td>
                    </tr>
                </tbody>
            </table>

            <div className="ws-blue ws-bold text-xs py-0.5 border-b border-ws-cell-border flex items-center gap-2 px-1">
                <span>ADDS: (items bolded below that were DELETIONS above can be added)</span>
                <UnderlineButton
                    variant="primary"
                    className="ms-auto shrink-0"
                    text="Reset to Defaults"
                    onClick={() => setPrefill((prev) => ({
                        ...prev,
                        ...getAllowanceAddFieldDefaults(prev.isSingleWide, prev.addNumberOfSets, prev.addSkirtingType),
                    }))}
                />
            </div>
            <table className="w-full text-xs border-collapse">
                <tbody>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell ws-bold">Factory Freight:  Homesite  or  Retailer Lot</td>
                        <td className="ws-cell w-8 text-right">$</td>
                        <td className="ws-cell w-24">
                            <WorksheetInput
                                value={prefill.addFactoryFreight ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addFactoryFreight: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell ws-bold">Air Conditioner:</td>
                        <td className="ws-cell w-8 text-right">$</td>
                        <td className="ws-cell w-24">
                            <WorksheetInput
                                value={prefill.addAirConditioner ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addAirConditioner: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell ws-bold">Tax from Invoice:</td>
                        <td className="ws-cell w-8 text-right">$</td>
                        <td className="ws-cell w-24">
                            <WorksheetInput
                                value={prefill.addTaxFromInvoice ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addTaxFromInvoice: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell ws-bold">HUD Dues/Fees:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell w-20">
                            <WorksheetInput
                                value={prefill.addHudDuesFees ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addHudDuesFees: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Delivery & Set:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addDeliveryAndSet ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addDeliveryAndSet: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Skirting Type:</td>
                        <td className="ws-cell" colSpan={2}>
                            <select
                                className="form-select form-select-sm"
                                value={prefill.addSkirtingType ?? ''}
                                onChange={(e) => {
                                    const type = e.target.value;
                                    const allowance = getSkirtingAllowanceByType(type, prefill.isSingleWide);
                                    setPrefill((prev) => ({
                                        ...prev,
                                        addSkirtingType: type,
                                        addSkirting: allowance != null ? String(allowance) : (prev.addSkirting ?? ''),
                                    }));
                                }}
                            >
                                <option value="">— Select —</option>
                                {SKIRTING_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Skirting: </td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addSkirting ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addSkirting: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Steps/Decks: &nbsp;(# of sets <WorksheetInput
                            className="ws-input-box w-6 inline"
                            value={prefill.addNumberOfSets ?? ''}
                            onChange={val => {
                                const sets = parseInt(val, 10) || 0;
                                const allowance = getAllowanceByItemName('Steps & Decks (Per Set)', prefill.isSingleWide) ?? 0;
                                setPrefill(prev => ({ ...prev, addNumberOfSets: val, addStepsDecks: String(allowance * sets) }));
                            }}
                            isInventoryField
                        /> )</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addStepsDecks ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addStepsDecks: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Dirt Pad:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addDirtPad ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addDirtPad: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Footers:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addFooters ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addFooters: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell ws-bold">Trim Out/Tape & Texture:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addTrimOutTapeAndTexture ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addTrimOutTapeAndTexture: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Electric Hookup:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addElectricHookup ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addElectricHookup: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Water Hookup:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addWaterHookup ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addWaterHookup: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Sewer Hookup:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addSewerHookup ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addSewerHookup: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Gas Hookup:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addGasHookup ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addGasHookup: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">Gutters & Downspouts:</td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addGuttersAndDownspouts ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addGuttersAndDownspouts: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell">
                            Other: &nbsp;
                            <WorksheetInput
                                value={prefill.addOther ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addOther: val }))}
                                isInventoryField
                            />
                        </td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.addOtherAmount ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, addOtherAmount: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">Total Adds:</td>
                        <td className="ws-cell ws-bold text-right">(F)$</td>
                        <td className="ws-cell ws-bold">
                            <input type="text" className="ws-input" readOnly value={totalAdds.toFixed(2)} />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">Total Home Value: &nbsp;&nbsp;(E+F=G)</td>
                        <td className="ws-cell ws-bold text-right">(G)$</td>
                        <td className="ws-cell ws-bold"><input type="text" className="ws-input" readOnly value={totalHomeValue.toFixed(2)} /></td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell ws-bold">Sales Tax (not on Invoice) :</td>
                        <td className="ws-cell text-right">(H)$</td>
                        <td className="ws-cell">
                            <WorksheetInput
                                value={prefill.maxSalesPriceSalesTax ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, maxSalesPriceSalesTax: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell ws-bold">Maximum Sales Price of Home (G+H=I)</td>
                        <td className="ws-cell ws-bold text-right">(I)$</td>
                        <td className="ws-cell ws-bold"><input type="text" className="ws-input" readOnly value={maximumSalesPriceOfHome.toFixed(2)} /></td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

// Land improvements grid: [leftLabel, rightLabel, leftKey, rightKey]
const LAND_IMPROVEMENT_ROWS = [
    ['Basement:', 'Grading:', 'landBasement', 'landGrading'],
    ['Brick/Block Skirting:', 'Permanent Foundation:', 'landBrickBlockSkirting', 'landPermanentFoundation'],
    ['Carport:', 'Permits:', 'landCarport', 'landPermits'],
    ['Deck:', 'Porch:', 'landDeck', 'landPorch'],
    ['Driveway:', 'Septic:', 'landDriveway', 'landSeptic'],
    ['Electrical Hookup:', 'Sewer Hookup:', 'landElectricalHookup', 'landSewerHookup'],
    ['Footers:', 'Water Hookup:', 'landFooters', 'landWaterHookup'],
    ['Garage:', 'Well:', 'landGarage', 'landWell'],
    ['Gas Hookup:', 'Other:', 'landGasHookup', 'landOther'],
];
const LAND_IMPROVEMENT_KEYS = LAND_IMPROVEMENT_ROWS.flatMap(([, , left, right]) => [left, right]);

// 2. LandWithImprovements — Land Purchase Price (J) + LAND IMPROVEMENTS + Total (K)
const LandWithImprovements = ({ prefill, setPrefill }) => {
    const totalImprovements = LAND_IMPROVEMENT_KEYS.reduce((sum, key) => sum + parseDecimal(prefill[key]), 0);

    return (
        <div className="border border-ws-cell-border">
            <div className="ws-header text-xs">FOR LAND / HOME LOANS Complete Section Below</div>
            <table className="w-full text-xs border-collapse">
                <tbody>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell ws-bold">Land Purchase Price or Payoff:</td>
                        <td className="ws-cell text-right">(J)$</td>
                        <td className="ws-cell w-20">
                            <WorksheetInput
                                value={prefill.landPurchasePrice ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, landPurchasePrice: val }))}
                                isInventoryField
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <div className=" text-lg ws-blue ws-bold ">LAND IMPROVEMENTS:</div>
            <table className="w-full text-xs border-collapse">
                <tbody>
                    {LAND_IMPROVEMENT_ROWS.map(([leftLabel, rightLabel, leftKey, rightKey], i) => (
                        <tr key={i} className="border-b border-ws-cell-border">
                            <td className="ws-cell">{leftLabel}</td>
                            <td className="ws-cell w-12">
                                <WorksheetInput className="ws-input w-10 inline" value={prefill[leftKey] ?? ''} onChange={val => setPrefill(prev => ({ ...prev, [leftKey]: val }))} isInventoryField />
                            </td>
                            <td className="ws-cell">{rightLabel}</td>
                            <td className="ws-cell w-12">
                                <WorksheetInput className="ws-input w-10 inline" value={prefill[rightKey] ?? ''} onChange={val => setPrefill(prev => ({ ...prev, [rightKey]: val }))} isInventoryField />
                            </td>
                        </tr>
                    ))}
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold" colSpan={2}>Total Improvements:</td>
                        <td className="ws-cell ws-bold text-right">(K)$</td>
                        <td className="ws-cell">
                            <input type="text" className="ws-input" readOnly value={totalImprovements.toFixed(2)} />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

// 3. TotalMaximumSalesPrice — TOTAL Maximum Package Price (I+J+K) + footer
// I = Maximum Sales Price of Home, J = Land Purchase Price or Payoff, K = Total Improvements
const TotalMaximumSalesPrice = ({ prefill }) => {
    const totalDeletions = DELETION_KEYS.reduce((sum, key) => sum + parseDecimal(prefill[key]), 0);
    const invoiceAmount = parseDecimal(prefill.invoice);
    const newHomeNetInvoice = invoiceAmount - totalDeletions;
    const vepAdjustmentPoints = getVepAdjustmentPoints(prefill.vepCode);
    const adjustedMarkupPct = 145 + vepAdjustmentPoints;
    const adjustedMarkupAmount = newHomeNetInvoice * (adjustedMarkupPct / 100);
    const totalAdds = ADD_AMOUNT_KEYS.reduce((sum, key) => sum + parseDecimal(prefill[key]), 0);
    const totalHomeValue = adjustedMarkupAmount + totalAdds;
    const salesTaxNotOnInvoice = parseDecimal(prefill.maxSalesPriceSalesTax);
    const maximumSalesPriceOfHome = totalHomeValue + salesTaxNotOnInvoice; // I

    const landPurchasePrice = parseDecimal(prefill.landPurchasePrice); // J
    const totalImprovements = LAND_IMPROVEMENT_KEYS.reduce((sum, key) => sum + parseDecimal(prefill[key]), 0); // K

    const totalMaximumPackagePrice = maximumSalesPriceOfHome + landPurchasePrice + totalImprovements;

    return (
        <div className="border border-ws-cell-border">
            <div className="ws-yellow-bg border border-ws-cell-border">
                <div className="ws-bold ws-blue text-center py-1">TOTAL Maximum Package Price (I+J+K): (home / land / improvements) &nbsp;&nbsp;&nbsp;$ <input type="text" className="ws-input-box w-24 inline" readOnly value={totalMaximumPackagePrice.toFixed(2)} /></div>
            </div>

            <div className="text-[9px] text-muted-foreground px-1 py-0.5">
                Rev. 9-2-2021 / 21st Sales Proposal &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; NMLS 2280
            </div>
        </div>
    );
};

// SiteOfPlacement Component — 4 rows: Site of Placement, Monthly Site Payment + community input, Value/Home Type/Phone, New-Used with values below
const SiteOfPlacement = ({ prefill, setPrefill, appChoices = [], modelOptions = [], modelOptionValues = [], onManufacturerChange, onModelChange }) => {
    const manufacturerOptions = (appChoices.find(a => a.key === 'manufacturer')?.values || []).map(m => ({
        label: m.factoryShortName || m.name || '-',
        value: m._id
    }));

    return (
        <div className="border border-ws-cell-border">
            {/* Row 1: Site Of Placement + 6 checkboxes in 2 rows of 3, equally spread */}
            <div className="flex items-center border-b border-ws-cell-border py-0.5">
                <span className="ws-bold px-1 whitespace-nowrap">Site Of Placement</span>
                <div className="flex-1 ws-site-placement-grid">
                    {SITE_OF_PLACEMENT_CHECKBOXES.map(({ key, label }) => (
                        <label key={key} className="ws-site-placement-label flex items-center gap-1 justify-center">
                            <input
                                type="checkbox"
                                className="ws-checkbox"
                                checked={!!prefill.siteOfPlacementFields?.[key]}
                                onChange={e => {
                                    const checked = e.currentTarget.checked;
                                    setPrefill(prev => patchSiteOfPlacementFields(prev, { [key]: checked }));
                                }}
                            />
                            {label}
                        </label>
                    ))}
                </div>
            </div>
            {/* Row 2: Two equal halves — left: Monthly Site Payment + input; right: Name of Community... + input */}
            <div className="flex border-b border-ws-cell-border">
                <div className="flex items-center gap-1 flex-1 border-r border-ws-cell-border px-1 py-0.5">
                    <span className="ws-bold whitespace-nowrap">Monthly Site Payment:</span>
                    <WorksheetInput
                        value={prefill.siteOfPlacementFields?.monthlySitePayment ?? ''}
                        onChange={val => setPrefill(prev => patchSiteOfPlacementFields(prev, { monthlySitePayment: val }))}
                        isInventoryField
                        className="ws-input flex-1"
                    />
                </div>
                <div className="flex items-center gap-1 flex-1 px-1 py-0.5">
                    <span className="ws-label">Name of Community, Family Member, Mortgage Holder or Reservation:</span>
                    <WorksheetInput
                        value={prefill.siteOfPlacementFields?.communityFamilyMortgageHolderReservationName ?? ''}
                        onChange={val =>
                            setPrefill(prev =>
                                patchSiteOfPlacementFields(prev, { communityFamilyMortgageHolderReservationName: val })
                            )
                        }
                        isInventoryField
                        className="ws-input flex-1"
                    />
                </div>
            </div>
            <div className="ws-row3-two-col border-b border-ws-cell-border">
                <div className="ws-row3-cell flex items-center gap-2 px-1 flex-wrap">
                    <span className="ws-bold whitespace-nowrap">Home Type</span>
                    {WORKSHEET_HOME_TYPE_RADIO_OPTIONS.map(({ value, label }) => (
                        <label key={value} className="ws-site-placement-label flex items-center gap-1">
                            <input
                                type="radio"
                                name="calculation-worksheet-home-type"
                                className="ws-checkbox"
                                checked={prefill.typeOfUnit === value}
                                onChange={() => setPrefill(prev => ({ ...prev, typeOfUnit: value }))}
                            />
                            {label}
                        </label>
                    ))}
                </div>
                <div className="ws-row3-cell flex items-center gap-1 px-1">
                    <span className="ws-label whitespace-nowrap">Phone Number:</span>
                    <WorksheetInput value={prefill.cellPhone || ''} onChange={() => { }} isInventoryField className="ws-input flex-1 min-w-0" />
                </div>
            </div>
            {/* Row 4: Six equal columns — label above input in each */}
            <div className="ws-row4-six-col border-b border-ws-cell-border">
                <div className="ws-row4-cell">
                    <span className="ws-bold text-xs">New / Used</span>
                    <select
                        className="form-select form-select-sm ws-input w-full"
                        value={prefill.type ?? ''}
                        onChange={e => { const v = e.target.value; setPrefill(prev => ({ ...prev, type: v })); }}
                    >
                        <option value="">— Select —</option>
                        <option value="New">New</option>
                        <option value="Used">Used</option>
                    </select>
                </div>
                <div className="ws-row4-cell">
                    <span className="ws-label text-xs">Repo ID #</span>
                    <WorksheetInput value={prefill.repoId ?? ''} onChange={val => setPrefill(prev => ({ ...prev, repoId: val }))} isInventoryField className="ws-input w-full" />
                </div>
                <div className="ws-row4-cell">
                    <span className="ws-label text-xs">Year</span>
                    <WorksheetInput value={prefill.year || ''} onChange={val => setPrefill(prev => ({ ...prev, year: val }))} isInventoryField className="ws-input w-full" />
                </div>
                <div className="ws-row4-cell">
                    <span className="ws-label text-xs">Make</span>
                    <select
                        className="form-select form-select-sm ws-input w-full"
                        value={prefill.manufacturer || ''}
                        onChange={e => onManufacturerChange?.(e.target.value)}
                    >
                        <option value="">— Select Manufacturer —</option>
                        {manufacturerOptions.map(({ label, value }) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>
                <div className="ws-row4-cell">
                    <span className="ws-label text-xs">Model</span>
                    <select
                        className="form-select form-select-sm ws-input w-full"
                        value={prefill.modelId || ''}
                        onChange={e => onModelChange?.(e.target.value)}
                        disabled={!prefill.manufacturer}
                    >
                        <option value="">— Select Model —</option>
                        {modelOptions.map((opt, i) => (
                            <option key={modelOptionValues[i] || i} value={modelOptionValues[i] || ''}>{opt}</option>
                        ))}
                    </select>
                </div>
                <div className="ws-row4-cell">
                    <span className="ws-label text-xs">Width x Length</span>
                    <WorksheetInput value={prefill.sizeOverall || ''} onChange={val => setPrefill(prev => ({ ...prev, sizeOverall: val }))} isInventoryField className="ws-input w-full" />
                </div>
            </div>
        </div>
    );
};

/** USED HOME / 21st REPO MSP — adds persist on Contact.amountDesiredCalculatorAddFields */
const UsedHomeMSP = ({ prefill, setPrefill }) => {
    const allMspAddBlank = MSP_ADD_AMOUNT_KEYS.every(k => isWorksheetBlank(prefill[k]));
    const totalMspAdds = MSP_ADD_AMOUNT_KEYS.reduce((s, k) => s + parseDecimal(prefill[k]), 0);
    const totalAddsDisplay = allMspAddBlank ? '' : String(totalMspAdds);

    // Total Home Value (C): Total Markup (A) is treated as 0 here → C = B (Total Adds only).
    const cTotal = allMspAddBlank ? null : totalMspAdds;
    const totalHomeValueDisplay = cTotal === null ? '' : String(cTotal);

    const salesTaxDisplay = isWorksheetBlank(prefill.amountDesiredSalesTax)
        ? ''
        : String(parseDecimal(prefill.amountDesiredSalesTax));

    const hasD = !isWorksheetBlank(prefill.amountDesiredSalesTax);
    const maxMspDisplay =
        cTotal === null && !hasD
            ? ''
            : String((cTotal ?? 0) + (hasD ? parseDecimal(prefill.amountDesiredSalesTax) : 0));

    return (
        <div className="border border-ws-cell-border">
            <div className="ws-header text-sm">USED HOME or 21st REPO Maximum Sales Price Calculation (MSP)</div>
            <table className="w-full text-xs border-collapse">
                <tbody>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell" colSpan={2}>
                            <label className="flex items-center gap-1">
                                <input type="checkbox" className="ws-checkbox" />
                                <span className="ws-bold">Used home sold on site - BASE NADA</span>
                            </label>
                            <div className="text-[9px] text-muted-foreground">(Base NADA x 140%) = MSP</div>
                        </td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell w-20"><input type="text" className="ws-input" /></td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold" colSpan={2}>Maximum Sales Price of Home</td>
                        <td className="ws-cell text-right">= $</td>
                        <td className="ws-cell"><input type="text" className="ws-input" /></td>
                        <td className="ws-cell ws-bold">x 140%</td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell" colSpan={2}>
                            <label className="flex items-center gap-1">
                                <input type="checkbox" className="ws-checkbox" />
                                <span className="ws-bold">Used home to be delivered - BASE NADA</span>
                            </label>
                            <div className="text-[9px] text-muted-foreground">(Base NADA x 130%) + ADDS = (MSP)</div>
                        </td>
                        <td className="ws-cell text-right">$</td>
                        <td className="ws-cell"><input type="text" className="ws-input" /></td>
                        <td className="ws-cell ws-bold">x 130%</td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold" colSpan={2}>Total Markup</td>
                        <td className="ws-cell text-right">= $</td>
                        <td className="ws-cell ws-bold">
                            <WorksheetInput
                                className="ws-input w-20 inline"
                                value={prefill.mspRepoMarkupA ?? ''}
                                onChange={val => setPrefill(prev => ({ ...prev, mspRepoMarkupA: val }))}
                                isInventoryField
                            />
                        </td>
                        <td className="ws-cell ws-bold">(A)</td>
                    </tr>
                    <tr className="border-b border-ws-cell-border">
                        <td className="ws-cell" colSpan={2}>&nbsp;&nbsp;21st Repo - Net Sales Price</td>
                        <td className="ws-cell"></td>
                        <td className="ws-cell">
                            <input type="text" className="ws-input w-20 inline" readOnly value={prefill.mspRepoMarkupA ?? ''} />
                        </td>
                        <td className="ws-cell">(A)</td>
                    </tr>
                </tbody>
            </table>

            {/* ADDS sub-section — left column CRM contact; right column inventory unit */}
            <div className="ws-blue ws-bold text-xs px-1 border-b border-ws-cell-border">ADDS:</div>
            <table className="w-full text-xs border-collapse">
                <tbody>
                    {MSP_ADD_DUAL_ROWS.map(([leftLabel, leftKey, rightLabel, rightKey]) => (
                        <tr key={leftKey} className="border-b border-ws-cell-border">
                            <td className="ws-cell">{leftLabel}</td>
                            <WorksheetPrefillTd prefill={prefill} setPrefill={setPrefill} fieldKey={leftKey} />
                            <td className="ws-cell">{rightLabel}</td>
                            <WorksheetPrefillTd prefill={prefill} setPrefill={setPrefill} fieldKey={rightKey} />
                        </tr>
                    ))}
                    {MSP_ADD_OTHER_ROWS.map(([leftLabel, leftKey, otherLabelKey, otherAmountKey]) => (
                        <tr key={leftKey} className="border-b border-ws-cell-border">
                            <td className="ws-cell">{leftLabel}</td>
                            <WorksheetPrefillTd prefill={prefill} setPrefill={setPrefill} fieldKey={leftKey} />
                            <WorksheetPrefillTd
                                prefill={prefill}
                                setPrefill={setPrefill}
                                fieldKey={otherLabelKey}
                                inputClass="ws-input w-28 inline"
                                placeholder="Other"
                            />
                            <WorksheetPrefillTd
                                prefill={prefill}
                                setPrefill={setPrefill}
                                fieldKey={otherAmountKey}
                                inputClass="ws-input w-16 inline"
                            />
                        </tr>
                    ))}
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">Total Adds:</td>
                        <td className="ws-cell ws-bold">(B)$</td>
                        <td className="ws-cell ws-bold text-right" colSpan={2}>
                            <ReadOnlyWorksheetValue value={totalAddsDisplay} />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">Total Home Value:</td>
                        <td className="ws-cell ws-bold">(A+B=C)</td>
                        <td className="ws-cell ws-bold">(C)$</td>
                        <td className="ws-cell">
                            <ReadOnlyWorksheetValue value={totalHomeValueDisplay} />
                        </td>
                    </tr>
                    <tr className="border-b border-ws-cell-border ">
                        <td className="ws-cell ws-bold">Sales Tax:</td>
                        <td className="ws-cell"></td>
                        <td className="ws-cell ws-bold">(D)$</td>
                        <td className="ws-cell">
                            <ReadOnlyWorksheetValue value={salesTaxDisplay} tabIndex={-1} />
                        </td>
                    </tr>
                    <tr className="ws-header ws-blue">
                        <td className="ws-cell ws-bold" colSpan={2}>Maximum Sales Price of Home &nbsp;&nbsp;(C+D)</td>
                        <td className="ws-cell ws-bold" colSpan={2}>
                            <ReadOnlyWorksheetValue value={maxMspDisplay} className="ws-input w-full inline" tabIndex={-1} />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const CalculationWorksheet = ({
    inventory,
    contact = null,
    contract = null,
    appChoices = [],
    onInventoryUpdate,
    onContactUpdate,
    onContractUpdate
}) => {
    /** When `contract` is passed (contract pipeline), CRM overlay reads/writes contract.*; otherwise contact.* */
    const crmRecord = contract ?? contact;
    const persistToContract = Boolean(contract?._id);

    const crmSiteSig =
        crmRecord == null
            ? ''
            : JSON.stringify({
                site: crmRecord.siteOfPlacementFields ?? null,
                monthlySitePayment: crmRecord.monthlySitePayment ?? null,
                communityFamilyMortgageHolderReservationName:
                    crmRecord.communityFamilyMortgageHolderReservationName ?? null
            });
    const amountDesiredCrmSig =
        crmRecord == null
            ? ''
            : JSON.stringify({
                salesPrice: crmRecord.salesPrice,
                downPayment: crmRecord.downPayment,
                insuranceCosts: crmRecord.insuranceCosts,
                adc: crmRecord.amountDesiredCalculatorFields ?? null,
                lieu: crmRecord.lieuEquityCalculatorFields ?? null,
                adcAdds: crmRecord.amountDesiredCalculatorAddFields ?? null,
                repoMsp: crmRecord.twentyFirstRepoMspCalculatorFields ?? null,
                tradeAf:
                    crmRecord.additionalFields && typeof crmRecord.additionalFields === 'object'
                        ? {
                            tradeYear: crmRecord.additionalFields.tradeYear,
                            tradeMakeModel: crmRecord.additionalFields.tradeMakeModel,
                            tradeSize: crmRecord.additionalFields.tradeSize,
                        }
                        : null,
            });
    const crmRecordIdForMemo = crmRecord?._id;
    const crmPhoneForMemo = crmRecord?.cellPhone;
    const mergedPrefill = useMemo(() => {
        const inventoryPrefill = buildInventoryPrefill(inventory);
        const cellPhoneFromCrm =
            crmRecord?.cellPhone != null && String(crmRecord.cellPhone).trim() !== ''
                ? String(crmRecord.cellPhone).trim()
                : '';
        return {
            ...inventoryPrefill,
            ...mergeAmountDesiredFieldsFromCrmRecord(crmRecord),
            ...mergeLieuEquityFieldsFromCrmRecord(crmRecord),
            ...mergeAmountDesiredCalculatorAddFieldsFromCrmRecord(crmRecord),
            ...mergeTwentyFirstRepoMspFromCrmRecord(crmRecord),
            ...mergeTradeAdditionalFieldsFromCrmRecord(crmRecord),
            ...(cellPhoneFromCrm !== '' ? { cellPhone: cellPhoneFromCrm } : {}),
            siteOfPlacementFields: mergeSiteOfPlacementFieldsFromCrmRecord(crmRecord)
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventory, crmRecordIdForMemo, crmSiteSig, crmPhoneForMemo, amountDesiredCrmSig]);
    const [prefill, setPrefill] = useState(mergedPrefill);
    const [modelOptions, setModelOptions] = useState([]);
    const [modelOptionValues, setModelOptionValues] = useState([]);

    // If inventory or CRM record changes while sidebar is open, sync prefill
    useEffect(() => {
        setPrefill(mergedPrefill);
    }, [mergedPrefill]);

    const inventoryModel = inventory?.model;
    // Load model options when manufacturer changes or on initial load
    useEffect(() => {
        if (!appChoices?.length) return;
        const manufacturerId = prefill.manufacturer;
        const populatedModel = inventoryModel && typeof inventoryModel === 'object' ? inventoryModel : null;
        const { options, values } = getModelOptions(appChoices, manufacturerId, populatedModel);
        setModelOptions(options);
        setModelOptionValues(values);
    }, [appChoices, prefill.manufacturer, inventoryModel]);

    const onManufacturerChange = (manufacturerId) => {
        const { options, values } = getModelOptions(appChoices, manufacturerId);
        setModelOptions(options);
        setModelOptionValues(values);
        const firstModelId = values?.[0] || '';
        setPrefill(prev => ({
            ...prev,
            manufacturer: manufacturerId,
            modelId: firstModelId,
            model: options?.[0] || '',
            sizeOverall: ''
        }));
        if (firstModelId) onModelChange(firstModelId);
    };

    const onModelChange = (modelId) => {
        const allModels = (appChoices.find(a => a.key === 'model')?.values || []).filter(Boolean);
        const model = allModels.find(m => m._id === modelId);
        if (!model) return;
        setPrefill(prev => {
            const next = { ...prev, modelId, model: [model?.modelName, model?.modelNumber].filter(Boolean).join(' - ') || '' };
            if (model.boxSize) {
                next.sizeOverall = model.boxSize;
            }
            if (model.numberOfSections !== undefined) {
                const sizes = updateSizesAccordingToSections(model.boxSize || prev.sizeOverall, Number(model.numberOfSections));
                Object.assign(next, sizes);
            }
            return next;
        });
    };

    const [saving, setSaving] = useState(false);
    const [printingDocument, setPrintingDocument] = useState(false);
    const [createdDocument, setCreatedDocument] = useState(null);
    const [viewingFile, setViewingFile] = useState(null);

    const abortControllerRef = useRef(null);

    const saveWorksheet = async ({ signal, silent = false } = {}) => {
        if (!inventory || !inventory._id || typeof onInventoryUpdate !== 'function') return false;
        const payload = buildWorksheetPayload(prefill);

        // Only update additionalFields.repoId; merge with existing to preserve other additionalFields
        const existingAdditionalFields =
            inventory.additionalFields || get(inventory, 'inventoryUnit.additionalFields', {}) || {};
        payload.additionalFields = {
            ...(typeof existingAdditionalFields === 'object' && existingAdditionalFields !== null ? existingAdditionalFields : {})
        };
        if (prefill.repoId != null && prefill.repoId !== '') {
            payload.additionalFields.repoId = String(prefill.repoId);
        } else if ('repoId' in payload.additionalFields) {
            delete payload.additionalFields.repoId;
        }

        // Include manufacturer, model, year, sizeOverall, type when changed in worksheet
        if (prefill.manufacturer) payload.manufacturer = prefill.manufacturer;
        if (prefill.modelId) payload.model = prefill.modelId;
        if (prefill.year != null && prefill.year !== '') payload.year = parseInt(prefill.year, 10) || undefined;
        if (prefill.sizeOverall != null && prefill.sizeOverall !== '') payload.sizeOverall = String(prefill.sizeOverall).trim() || undefined;
        if (prefill.type != null && prefill.type !== '') payload.type = String(prefill.type).trim() || undefined;
        if (prefill.vepCode != null && prefill.vepCode !== '') payload.vepCode = String(prefill.vepCode).trim() || undefined;

        if (!silent) {
            setSaving(true);
            toast.info('Saving calculation worksheet...');
        }

        const { response, error } = await makeApiRequests({
            requestBody: payload,
            method: 'PUT',
            endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id),
            signal
        });

        if (!silent) setSaving(false);

        if (error) {
            if (!silent && error !== ABORT_ERROR) toast.error(error);
            return false;
        }

        const inventoryResponse = response;

        const crmSidePayload = {
            siteOfPlacementFields: buildSiteOfPlacementNestedPayload(prefill.siteOfPlacementFields),
            ...buildMonthlySitePaymentRootPayload(prefill.siteOfPlacementFields),
        };
        const tradeAdditionalPersist =
            crmRecord?._id ? buildCrmAdditionalFieldsTradePersistPayload(prefill, crmRecord) : null;

        if (persistToContract && typeof onContractUpdate === 'function') {
            const amountDesiredPersist = buildAmountDesiredCrmPersistPayload(prefill, contract);
            const { response: contractResp, error: contractErr } = await makeApiRequests({
                requestBody: {
                    ...crmSidePayload,
                    ...amountDesiredPersist,
                    ...(tradeAdditionalPersist || {}),
                },
                method: 'PUT',
                endpoint: ENDPOINTS.CONTRACTS_WITH_ID(contract._id),
                signal
            });
            if (contractErr) {
                if (!silent && contractErr !== ABORT_ERROR) {
                    toast.error(`${contractErr} (inventory was saved.)`);
                }
                onInventoryUpdate(inventoryResponse);
            } else {
                const savedNestedPayload = buildSiteOfPlacementNestedPayload(prefill.siteOfPlacementFields);
                const savedRootSitePayload = buildMonthlySitePaymentRootPayload(prefill.siteOfPlacementFields);
                onContractUpdate({
                    ...contractResp,
                    siteOfPlacementFields: {
                        ...savedNestedPayload,
                        ...(contractResp?.siteOfPlacementFields || {}),
                    },
                    monthlySitePayment: contractResp?.monthlySitePayment ?? savedRootSitePayload.monthlySitePayment,
                    communityFamilyMortgageHolderReservationName:
                        contractResp?.communityFamilyMortgageHolderReservationName ??
                        savedRootSitePayload.communityFamilyMortgageHolderReservationName,
                    salesPrice: contractResp?.salesPrice ?? amountDesiredPersist.salesPrice,
                    downPayment: contractResp?.downPayment ?? amountDesiredPersist.downPayment,
                    insuranceCosts: contractResp?.insuranceCosts ?? amountDesiredPersist.insuranceCosts,
                    amountDesiredCalculatorFields: {
                        ...amountDesiredPersist.amountDesiredCalculatorFields,
                        ...(contractResp?.amountDesiredCalculatorFields || {}),
                    },
                    lieuEquityCalculatorFields: {
                        ...amountDesiredPersist.lieuEquityCalculatorFields,
                        ...(contractResp?.lieuEquityCalculatorFields || {}),
                    },
                    amountDesiredCalculatorAddFields: {
                        ...amountDesiredPersist.amountDesiredCalculatorAddFields,
                        ...(contractResp?.amountDesiredCalculatorAddFields || {}),
                    },
                    twentyFirstRepoMspCalculatorFields: {
                        ...amountDesiredPersist.twentyFirstRepoMspCalculatorFields,
                        ...(contractResp?.twentyFirstRepoMspCalculatorFields || {}),
                    },
                    ...(tradeAdditionalPersist?.additionalFields
                        ? { additionalFields: tradeAdditionalPersist.additionalFields }
                        : {}),
                });
            }
        } else if (!persistToContract && contact?._id && typeof onContactUpdate === 'function') {
            const amountDesiredPersist = buildAmountDesiredCrmPersistPayload(prefill, contact);
            const { response: contactResp, error: contactErr } = await makeApiRequests({
                requestBody: {
                    ...crmSidePayload,
                    ...amountDesiredPersist,
                    ...(tradeAdditionalPersist || {}),
                },
                method: 'PUT',
                endpoint: ENDPOINTS.CONTACTS_WITH_ID(contact._id),
                signal
            });
            if (contactErr) {
                if (!silent && contactErr !== ABORT_ERROR) {
                    toast.error(`${contactErr} (inventory was saved.)`);
                }
                // Contact PUT failed — still push inventory response so UI matches DB
                onInventoryUpdate(inventoryResponse);
            } else {
                const savedNestedPayload = buildSiteOfPlacementNestedPayload(prefill.siteOfPlacementFields);
                const savedRootSitePayload = buildMonthlySitePaymentRootPayload(prefill.siteOfPlacementFields);
                onContactUpdate({
                    ...contactResp,
                    siteOfPlacementFields: {
                        ...savedNestedPayload,
                        ...(contactResp?.siteOfPlacementFields || {}),
                    },
                    monthlySitePayment: contactResp?.monthlySitePayment ?? savedRootSitePayload.monthlySitePayment,
                    communityFamilyMortgageHolderReservationName:
                        contactResp?.communityFamilyMortgageHolderReservationName ??
                        savedRootSitePayload.communityFamilyMortgageHolderReservationName,
                    salesPrice: contactResp?.salesPrice ?? amountDesiredPersist.salesPrice,
                    downPayment: contactResp?.downPayment ?? amountDesiredPersist.downPayment,
                    insuranceCosts: contactResp?.insuranceCosts ?? amountDesiredPersist.insuranceCosts,
                    amountDesiredCalculatorFields: {
                        ...amountDesiredPersist.amountDesiredCalculatorFields,
                        ...(contactResp?.amountDesiredCalculatorFields || {}),
                    },
                    lieuEquityCalculatorFields: {
                        ...amountDesiredPersist.lieuEquityCalculatorFields,
                        ...(contactResp?.lieuEquityCalculatorFields || {}),
                    },
                    amountDesiredCalculatorAddFields: {
                        ...amountDesiredPersist.amountDesiredCalculatorAddFields,
                        ...(contactResp?.amountDesiredCalculatorAddFields || {}),
                    },
                    twentyFirstRepoMspCalculatorFields: {
                        ...amountDesiredPersist.twentyFirstRepoMspCalculatorFields,
                        ...(contactResp?.twentyFirstRepoMspCalculatorFields || {}),
                    },
                    ...(tradeAdditionalPersist?.additionalFields
                        ? { additionalFields: tradeAdditionalPersist.additionalFields }
                        : {}),
                });
            }
        } else {
            onInventoryUpdate(inventoryResponse);
        }

        if (!silent) toast.success('Calculation worksheet saved.');
        return true;
    };

    const handleSave = () => saveWorksheet({ silent: false });

    const getCalculationWorksheetPrintDoc = () => {
        const CALC_WORKSHEET_DOC_ID = '1o-1M6H25J2fjrOAIcsIog2vO1HfM1WbAH1P1J2NmPOM';
        const printGroupsChoice = appChoices?.find(ac => ac.key === 'printGroups')?.values || [];
        if (!printGroupsChoice.length) return null;

        const candidate = printGroupsChoice.find(pg => pg?.url?.includes(CALC_WORKSHEET_DOC_ID));
        if (!candidate) return null;

        return { docId: CALC_WORKSHEET_DOC_ID, documentName: candidate.name };
    };

    const fetchDocumentParamsForPrint = async (docId, signal) => {
        const dataSource = contact ? 'Contact' : contract ? 'Contract' : 'Inventory';
        const dataObject = contact || contract || inventory;

        const { response, error } = await makeApiRequests({
            endpoint: ENDPOINTS.DOCHUB_DOCUMENT_PARAMS(dataObject._id),
            requestBody: {
                docId,
                dataSource,
            },
            signal
        });

        if (error) {
            if (error === ABORT_ERROR) return null;
            toast.error(error);
            return null;
        }

        const foundParams = response?.foundParams || [];
        const documentParams = foundParams.reduce((acc, p) => {
            if (!p?.name) return acc;
            acc[p.name] = p.value;
            return acc;
        }, {});


        documentParams.inventoryUnit = inventory._id;

        return documentParams;
    };

    const cancelPrinting = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        if (printingDocument?.printSessionId) {
            // Best-effort cancellation (server may handle it via print session id)
            makeApiRequests({
                endpoint: ENDPOINTS.DOCHUB_PRINT_DOCUMENT_CANCEL(printingDocument.printSessionId)
            });
        }

        setPrintingDocument(false);
        setCreatedDocument(null);
    };

    const onPrintClick = async () => {
        if (printingDocument) {
            cancelPrinting();
            return;
        }

        // Treat save+print as one operation: update UI immediately and avoid separate save toasts.
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setCreatedDocument(null);
        setPrintingDocument({ printSessionId: null });
        toast.info('Please wait, creating document...', { type: 'info' });

        const saved = await saveWorksheet({ signal: controller.signal, silent: true });
        if (!saved) {
            setPrintingDocument(false);
            return;
        }

        if (!inventory?._id) return;

        const template = getCalculationWorksheetPrintDoc();
        if (!template) {
            toast.error('Unable to find the “Max Sales Price” print template for Inventory in Print Groups.');
            setPrintingDocument(false);
            return;
        }

        const documentParams = await fetchDocumentParamsForPrint(template.docId, controller.signal);
        if (!documentParams) return;

        const { signal } = controller;
        const printSessionId = uuid();

        setPrintingDocument({ printSessionId });

        const dataSource = contact ? 'Contact' : contract ? 'Contract' : 'Inventory';
        const dataObject = contact || contract || inventory;

        const { response, error } = await makeApiRequests({
            endpoint: ENDPOINTS.DOCHUB_PRINT_DOCUMENT_WITH_ID(dataObject._id),
            method: 'POST',
            requestBody: {
                documentParams,
                docId: template.docId,
                documentName: template.documentName,
                dataSource,
                printSessionId
            },
            signal
        });

        if (error === ABORT_ERROR) return;

        if (error) {
            toast.error(error);
        } else {
            const originalName = response?.metadata?.originalName;
            setCreatedDocument({ originalName, ...response });
            setTimeout(() => window.scrollTo(0, document.body.scrollHeight), 100);
        }

        setPrintingDocument(false);
    };

    return (
        <div className="calculation-worksheet-container">
            {/* Site of Placement Section */}
            <SiteOfPlacement
                prefill={prefill}
                setPrefill={setPrefill}
                appChoices={appChoices}
                modelOptions={modelOptions}
                modelOptionValues={modelOptionValues}
                onManufacturerChange={onManufacturerChange}
                onModelChange={onModelChange}
            />

            {/* Main two-column layout */}
            <div className="grid grid-cols-2 gap-0">
                {/* Left Column */}
                <div>
                    <CalculationAmountDesired
                        prefill={prefill}
                        setPrefill={setPrefill}
                        tradeFieldsEditable={Boolean(crmRecord?._id)}
                    />
                    <LandEquity prefill={prefill} setPrefill={setPrefill} />
                    <UsedHomeMSP prefill={prefill} setPrefill={setPrefill} />
                </div>

                {/* Right Column */}
                <div>
                    <UnitMaxSalesPrice prefill={prefill} setPrefill={setPrefill} />
                    <LandWithImprovements prefill={prefill} setPrefill={setPrefill} />
                    <TotalMaximumSalesPrice prefill={prefill} />
                </div>
            </div>

            {onInventoryUpdate && (
                <div className="mt-3 mb-2 text-right">
                    <Button variant="success" size="sm" onClick={handleSave} disabled={saving || printingDocument}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={onPrintClick}
                        disabled={saving}
                        className="ml-2"
                    >
                        {printingDocument ? 'Printing document... Tap again to cancel' : 'Print'}
                    </Button>
                </div>
            )}

            {createdDocument && (
                <>
                    <Alert variant="success" className="my-3 d-flex align-items-center">
                        <p className="mb-0 flex-grow-1">
                            The document "{createdDocument.originalName}" has been created successfully. This has been added
                            to the respective Inventory folder. You may quickly access the file here.
                        </p>
                        <Button className="mx-2" variant="success" onClick={() => setViewingFile(createdDocument)}>
                            Open File
                        </Button>
                        <X size={25} className="ml-2 hover" onClick={() => setCreatedDocument(null)} />
                    </Alert>
                    {viewingFile && <FilePopup file={viewingFile} onHide={() => setViewingFile(null)} />}
                </>
            )}
        </div>
    );
};

export default CalculationWorksheet;
